import {useWbAgentClient} from '@wandb/weave/lib/WbAgent/useWbAgentClient';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {useViewer} from '../../state/viewer/hooks';

const ALLOWED_EMAILS = [
  'tim@wandb.com',
  'shawn@wandb.com',
  'zubin@wandb.com',
  'jzhao@coreweave.com',
  'jason.zhao@wandb.com',
];

type WbAgentChatContextState = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  entity: string | null;
  project: string | null;
  /** null = unknown/loading, true/false = resolved */
  available: boolean | null;
  setScope: (entity: string | null, project: string | null) => void;
};

const WbAgentChatContext = createContext<WbAgentChatContextState>({
  isOpen: false,
  open: () => {},
  close: () => {},
  toggle: () => {},
  entity: null,
  project: null,
  available: null,
  setScope: () => {},
});

export const useWbAgentChatContext = () => useContext(WbAgentChatContext);

export const WbAgentChatProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [entity, setEntity] = useState<string | null>(null);
  const [project, setProject] = useState<string | null>(null);
  const [available, setAvailable] = useState<boolean | null>(null);
  const viewer = useViewer();

  const client = useWbAgentClient(entity ?? undefined, project ?? undefined);

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen(prev => !prev), []);
  const setScope = useCallback((e: string | null, p: string | null) => {
    setEntity(e);
    setProject(p);
    // Close if we lose scope (navigated away from a project page)
    if (!e || !p) {
      setIsOpen(false);
    }
  }, []);

  // Check availability whenever entity/project change
  useEffect(() => {
    if (!viewer || !viewer.email || !ALLOWED_EMAILS.includes(viewer.email)) {
      setAvailable(false);
      return;
    }

    if (!client) {
      setAvailable(null);
      return;
    }
    let cancelled = false;
    client
      .get('/is-available')
      .then(r => r.available)
      .catch(() => false)
      .then(a => {
        if (!cancelled) {
          setAvailable(a);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [client, viewer]);

  const value = useMemo(
    () => ({isOpen, open, close, toggle, entity, project, available, setScope}),
    [isOpen, open, close, toggle, entity, project, available, setScope]
  );

  return (
    <WbAgentChatContext.Provider value={value}>
      {children}
    </WbAgentChatContext.Provider>
  );
};
